import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, defer, from, switchMap, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AuthService } from '../services/auth.service';
import { extractApiError } from '../utils/extract-api-error';
import { environment } from '@env/environment';
import { SKIP_AUTH } from './auth.interceptor';

/** Header flag marking a request that has already been retried once after a 401. */
const RETRY_HEADER = 'X-Auth-Retry';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH) || !req.url.startsWith(environment.services.api)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const messageService = inject(MessageService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        // Attempt a single refresh + retry before logging out (Bug #782580).
        // The retried request is not re-processed by this interceptor, so its
        // error is handled inline — a second 401 logs out instead of looping.
        return defer(() => from(auth.refreshIdToken())).pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              auth.logOut();
              return throwError(() => err);
            }
            return next(
              req.clone({
                setHeaders: {
                  Authorization: `Bearer ${auth.getIdToken()!}`,
                  [RETRY_HEADER]: '1',
                },
                withCredentials: true,
              }),
            ).pipe(
              catchError((retryErr: HttpErrorResponse) => {
                if (retryErr.status === 401) {
                  auth.logOut();
                }
                return throwError(() => retryErr);
              }),
            );
          }),
        );
      }

      if (err.status === 403) {
        const apiError = extractApiError(err);
        messageService.add({
          severity: 'error',
          summary: $localize`:@@access_denied:Access Denied`,
          detail: apiError.message || 'You do not have permission to perform this action.',
          life: 5000,
        });
        return throwError(() => err);
      }

      if (err.status === 500 || err.status === 503) {
        const apiError = extractApiError(err);
        messageService.add({
          severity: 'error',
          summary: $localize`:@@server_error:Server Error`,
          detail:
            apiError.message || 'An unexpected server error occurred. Please try again later.',
          life: 5000,
        });
        return throwError(() => err);
      }

      return throwError(() => err);
    }),
  );
};
